import { useEffect, useState } from "react";
import { Send, Instagram, Truck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Reveal, SectionKicker } from "./Reveal";
import { TikTokIcon } from "./Logo";
import { IG_PROFILE_URL, TIKTOK_URL } from "@/lib/constants";

export const ContactSection = ({ prefill, onPrefillConsumed }) => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (prefill) {
      setForm((f) => ({ ...f, message: prefill }));
      onPrefillConsumed();
    }
  }, [prefill, onPrefillConsumed]);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success(t.contact.success);
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error(t.contact.error);
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full border border-edge bg-obsidian px-4 py-3.5 font-grotesk text-sm text-ink placeholder:text-mutedink/60 transition-colors focus:border-volt focus:outline-none";

  return (
    <section id="kontakt" data-testid="contact-section" className="mx-auto max-w-7xl scroll-mt-24 px-5 py-24 md:px-8 md:py-32">
      <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
        <Reveal className="md:col-span-5">
          <SectionKicker>{t.contact.kicker}</SectionKicker>
          <h2 className="font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl lg:text-4xl" data-testid="contact-title">
            {t.contact.title}
          </h2>
          <p className="mt-5 max-w-sm text-sm leading-relaxed text-mutedink sm:text-base">{t.contact.sub}</p>
          <div className="mt-8 flex flex-wrap items-center gap-4 border-l-2 border-volt pl-4 font-mono text-xs uppercase tracking-widest text-mutedink">
            <a data-testid="contact-instagram-link" href={IG_PROFILE_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 transition-colors hover:text-volt">
              <Instagram size={13} /> @hellovintora
            </a>
            <a data-testid="contact-tiktok-link" href={TIKTOK_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 transition-colors hover:text-volt">
              <TikTokIcon size={12} /> @vintora8
            </a>
          </div>
          <div className="mt-4 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-mutedink" data-testid="contact-shipping-note">
            <Truck size={12} /> {t.contact.shipping}
          </div>
        </Reveal>
        <Reveal delay={0.15} className="md:col-span-7">
          <form onSubmit={submit} className="border border-edge bg-surface p-6 shadow-[0_2px_16px_rgba(18,20,23,0.05)] md:p-8" data-testid="contact-form">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <input
                data-testid="contact-name-input"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={t.contact.name}
                className={inputCls}
              />
              <input
                data-testid="contact-email-input"
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder={t.contact.email}
                className={inputCls}
              />
            </div>
            <textarea
              data-testid="contact-message-input"
              required
              rows={5}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder={t.contact.message}
              className={`${inputCls} mt-4 resize-none`}
            />
            <button
              data-testid="contact-submit-button"
              type="submit"
              disabled={sending}
              className="group mt-6 flex items-center gap-2 bg-volt px-7 py-4 font-grotesk text-sm font-bold uppercase tracking-widest text-obsidian transition-colors duration-300 hover:bg-volt-hover disabled:opacity-50"
            >
              {sending ? t.contact.sending : t.contact.send}
              <Send size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
